import type { ConnectionType } from '../types/connection.types';
import { CONNECTOR_LABEL } from '../utils/derive-platform-context';
import {
  resolveActiveCatalogImport,
  type ActiveCatalogImport,
} from '../utils/resolve-onboarding-redirect';

import { useOnboardingStatus } from './use-onboarding-status';

/** The active catalog import plus its display source label, or `null`. */
export type ActiveCatalogImportWithSource = ActiveCatalogImport & { source: string };

/** Fallback source label — the connector code isn't in {@link CONNECTOR_LABEL}. */
function sourceLabelFor(connectorCode: ConnectionType): string {
  return CONNECTOR_LABEL[connectorCode] ?? connectorCode;
}

/**
 * Detects a currently-running catalog import from the shared onboarding-status
 * cache (no extra request; the aggregate is already prefetched at login). Used
 * by the global progress widget to decide whether to show and where to poll /
 * navigate. Returns `null` until the status resolves and while no import runs.
 */
export function useActiveCatalogImport(): ActiveCatalogImportWithSource | null {
  const { data } = useOnboardingStatus();
  if (!data) return null;
  const active = resolveActiveCatalogImport(data);
  if (!active) return null;
  return { ...active, source: sourceLabelFor(active.connectorCode) };
}
